import React, { useRef, useState } from 'react';
import WeekTimecardGrid from './WeekTimecardGrid';
import useCalcPreview from './useCalcPreview';
import ConflictChooser from './ConflictChooser';
import { moneyOrAbsent } from '../../utils/moneyOrAbsent';
import {
  BLANK_FLAT_TITLE, WORKSHOP_ON_SET_WARNING, workshopOnShootingUnit, DAY_TYPE_OPTIONS, WORKING_DAY_OPTIONS, GRID_BOLD_FIELDS, GRID_ROW_GROUPS, OVERRIDE_FIELDS, FLAT_GREYED_FIELDS, REST_CLEARS_FIELDS, TYPED_AS_STORED_FIELDS, UNIT_FIELD, awayReasonLabel, controlValue, fieldKind, fieldLabel,
  fieldEditorProps, formatFieldValue, gridCellText, skipReasonText,
  markRequiredRows, PICKED_WORKING_DAYS, softDayComplete, unitNameIn,
} from '../../utils/timesheetDisplay';

/**
 * ONE PERSON'S WEEK on the shared `WeekTimecardGrid` — the manager's expanded
 * row (`mode="manager"`) and the crew member's own "My timesheet"
 * (`mode="crew"`). It replaced the day-tab strip + one-day form
 * (`PersonWeekPanel` / `DayFieldsForm`, deleted 2026-10-25).
 *
 * The two-value model reads as before (spec §5): a cell with no own value
 * FOLLOWS the unit (muted); an own value carries a dot and says whose in its
 * title; a CONFLICT is amber with ▲ — for a manager it opens the chooser
 * (`ConflictChooser`, the existing resolve request through `onResolve`), for
 * the crew member it is a read-only explanation ("the unit timecard says
 * 08:00 — a manager will choose") and the cell still edits their own value,
 * exactly the authority the crew screen had (no gate moved).
 *
 * Edits are DRAFTS keyed by date — `{ [date]: { fields, gagText?, notes? } }` —
 * owned by the caller, saved by the caller one PUT per dirty day with the
 * body `draftsToBodies` builds (the shape the one-day form sent). Typing a
 * value back to what the server holds drops it from the draft.
 *
 * 💷 Money: the manager's grey "Day cost" row prints the SERVER'S figure
 * (`cost.total`, or the committed snapshot of a submitted week). The crew mode
 * renders no money row and imports nothing that could.
 */

const SELECT_KINDS = new Set(['unit', 'day_type', 'working_day']);
/** What the calculation reads: a draft of any re-asks the server as you type (2026-10-26). */
const CALC_INPUTS = Object.freeze([UNIT_FIELD, 'day_type', 'working_day', 'unit_call', 'unit_wrap', 'meal_start', 'meal_end', 'in_time', 'out_time', 'flat_day']);
/** A draft of any of these can move a wrap worked out from THIS day's own call (2026-10-28). */
const AUTO_WRAP_INPUTS = Object.freeze([UNIT_FIELD, 'day_type', 'working_day', 'unit_call']);
const rowKind = (kind) => (SELECT_KINDS.has(kind) ? 'select' : kind);

export function draftsDirty(drafts) {
  return Object.values(drafts || {}).some((d) => d && (Object.keys(d.fields || {}).length > 0 || d.gagText !== undefined || d.notes !== undefined));
}

/**
 * The request body for ONE day's draft — only what changed. `gagByTitle`
 * maps a lower-cased title to its id. `{ body }`, or `{ error }` when the
 * job names no gag (the server links by id). The shape is the one-day form's,
 * unchanged (request-body parity, 2026-10-25).
 */
export function draftToBody(draft, day, gagByTitle) {
  const body = {};
  const fields = (draft && draft.fields) || {};
  const out = {};
  for (const [field, raw] of Object.entries(fields)) {
    const kind = fieldKind(field);
    // `null` clears for every kind (↺ on a meal-penalty override puts the calculation back).
    if (raw === null) out[field] = null;
    else if (kind === 'tick') out[field] = raw === true;
    else if (raw === null || raw === undefined || String(raw).trim() === '') out[field] = null;
    else out[field] = kind === 'unit' ? Number(raw) : String(raw).trim();
  }
  if (Object.keys(out).length) body.fields = out;
  if (draft && draft.gagText !== undefined) {
    const t = String(draft.gagText || '').trim();
    // A gag's title links the gag; anything else is a CUSTOM job (owner 2026-10-27: "the ability to
    // also type a custom job") — the server keeps one or the other, never both.
    if (t === '' || isJobDefault(t)) { body.gag_id = null; body.job_text = null; }
    else {
      const id = gagByTitle.get(t.toLowerCase());
      if (id == null) { if (!(day && day.job_text === t)) body.job_text = t; }
      else if (!(day && day.gag && Number(day.gag.id) === Number(id))) body.gag_id = id;
    }
  }
  if (draft && draft.notes !== undefined) body.notes = draft.notes;
  return { body };
}

/**
 * The Main job/task DEFAULT (owner 2026-10-30: "Maybe the first option in the main job/task is N/A or
 * various or default. so its not fully required but day type is", then "just change the first default
 * option from Various to Select... then various can be a ligitimate 2nd option"): a blank job SHOWS
 * "Select…" — encouraging a pick, never enforced — it is the first suggestion, and picking it stores
 * nothing. "Various" is a REAL job, the second suggestion, stored like any typed job.
 */
export const JOB_DEFAULT = 'Select…';
export const JOB_VARIOUS = 'Various';
// Only the PROMPT itself ("Select…" or "Select..."), never a bare "select" a person typed as a job
// (review U3, 2026-10-30).
const isJobDefault = (t) => /^select(…|\.\.\.)$/i.test(t);

/** The day's saved main job: its gag's title, else its custom job text, else ''. */
function savedJob(day) {
  if (day.gag) return day.gag.title || '';
  return day.job_text || '';
}

/** `[{ date, body }]` for every dirty day in date order, or `{ error }`. */
export function draftsToBodies(drafts, person, gagByTitle) {
  const out = [];
  for (const d of person.days) {
    const draft = drafts[d.date];
    if (!draft || !draftsDirty({ x: draft })) continue;
    const r = draftToBody(draft, d, gagByTitle);
    if (r.error) return { error: r.error };
    out.push({ date: d.date, body: r.body });
  }
  return { bodies: out };
}

/** This person's open conflicts as resolve items (the request's own shape). */
export function personConflictItems(person) {
  const out = [];
  for (const d of person.days || []) {
    for (const field of d.conflicts || []) out.push({ lineage_id: person.lineage_id, work_date: d.date, field });
  }
  return out;
}

export default function PersonWeekGrid({
  person,
  mode = 'crew',
  units = [],
  gagTitles = [],
  today,
  readOnly = false,
  drafts,
  onDraftsChange,
  onResolve,
  idPrefix = 'person-grid',
  onStepWeek = null, // 📱 the phone strip's end cards (WeekTimecardGrid)
  nextWeekOpen = true, // false = no "Next week ›" end card (My timesheet's latest open week)
  requestedWeek = null,
  jumpTo = null, // { date, n } — WeekTimecardGrid takes the person to that day
}) {
  const [chooserBusy, setChooserBusy] = useState(false);
  const [chooserError, setChooserError] = useState(null);
  const isManager = mode === 'manager';
  const byDate = new Map(person.days.map((d) => [d.date, d]));
  const dates = person.days.map((d) => d.date);
  const submitted = person.status === 'submitted';
  const unitName = (id) => unitNameIn(units, id);
  const unitOptions = [{ value: '', label: 'No department' }, ...units.map((u) => ({ value: String(u.id), label: u.name }))];
  const dayTypeOptions = [{ value: '', label: 'Not set' }, ...DAY_TYPE_OPTIONS];
  const workingDayOptions = [{ value: '', label: 'Flat (default)' }, ...WORKING_DAY_OPTIONS];

  const fieldRow = (f) => ({
    key: f,
    label: fieldLabel(f),
    kind: rowKind(fieldKind(f)),
    bold: GRID_BOLD_FIELDS.includes(f),
    options: fieldKind(f) === 'unit' ? unitOptions : fieldKind(f) === 'day_type' ? dayTypeOptions : fieldKind(f) === 'working_day' ? workingDayOptions : undefined,
    ...fieldEditorProps(f), // Travel (h): the 15-minute list, numbers only (2026-10-28)
  });
  // CALCULATED OVERTIME (2026-10-26, the owner-approved mock): the manager's view carries the
  // server's `day.overtime`; the crew view does not (this round), so its O/T rows stay typed fields.
  const hasCalc = isManager && person.days.some((d) => d.overtime);
  const [g0, g1, g2] = GRID_ROW_GROUPS.map((g) => g.map(fieldRow));
  // Owner 2026-10-25: Job directly under Unit ("move job to under unit");
  // Notes INSIDE the O/T fold ("O/T, penalties & notes").
  // Owner 2026-10-27: pick a gag OR type a custom job, with suggestions as you type ("Rain" →
  // "Rain Rig", "Rain stands", "Brackets for rain rig"): the gags, then custom jobs already used this week.
  const customJobs = [...new Set(person.days.map((d) => d.job_text).filter(Boolean))]
    .filter((t) => !gagTitles.some((g) => g.toLowerCase() === t.toLowerCase()));
  const jobRow = { key: 'gag', label: 'Main job/task', kind: 'suggest', options: [JOB_DEFAULT, JOB_VARIOUS, ...[...gagTitles, ...customJobs].filter((t) => t.toLowerCase() !== JOB_VARIOUS.toLowerCase() && !isJobDefault(t))] };
  const unitAt = g0.findIndex((r) => r.key === UNIT_FIELD);
  const dayGroup = [...g0.slice(0, unitAt + 1), jobRow, ...g0.slice(unitAt + 1)];
  // The standard day ("11+1") rides IN the Working day cell (owner 2026-10-27: "put the hours
  // after the working day type and save a whole row") — `withStandard` below.
  const otGroup = g2.map((r) => (hasCalc && OVERRIDE_FIELDS.includes(r.key) ? { ...r, noFill: true } : r));
  if (hasCalc) otGroup.splice(otGroup.findIndex((r) => r.key === 'meal_penalty') + 1, 0, { key: 'rest', label: 'Rest before', kind: 'computed' });
  if (hasCalc) otGroup.unshift({ key: 'ot_total', label: 'Total O/T', kind: 'computed' });
  otGroup.push({ key: 'notes', label: 'Notes', kind: 'text' });
  // Pre-call / Final out sit IN the times (GRID_ROW_GROUPS since 2026-10-28: Pre-call above Unit call,
  // Final out below Wrap), so ONE "Times" fold holds them all (owner 2026-10-27: a Flat week folds them).
  const groups = [dayGroup, g1, otGroup];
  if (isManager) groups.push([{ key: 'cost', label: submitted ? 'Day cost (actual)' : 'Day cost', kind: 'computed' }]);

  // 🪤 A Fill commits several days in ONE click, synchronously: each commit must
  // build on the one before it, not on the render's `drafts` (the last would win).
  const latest = useRef(drafts);
  latest.current = drafts;
  const draftOf = (date) => latest.current[date] || {};
  const setDraft = (date, next) => {
    const out = { ...latest.current };
    const clean = { ...next };
    if (clean.fields && Object.keys(clean.fields).length === 0) delete clean.fields;
    if (Object.keys(clean).length === 0) delete out[date]; else out[date] = clean;
    latest.current = out;
    onDraftsChange(out);
  };

  // AS YOU TYPE (owner, 2026-10-26): the drafted days through the server's calculator.
  const previewRequests = !hasCalc ? [] : person.days
    // NOT gated on the SAVED `day.worked` (owner 2026-10-27: a day typed in from blank showed no
    // O/T until saved) — the server's calculator decides whether the drafted day is worked.
    .filter((day) => drafts[day.date] && drafts[day.date].fields && CALC_INPUTS.some((f) => Object.prototype.hasOwnProperty.call(drafts[day.date].fields, f)))
    .map((day) => {
      const df = drafts[day.date].fields;
      const req = { key: day.date };
      for (const f of CALC_INPUTS) {
        const st = day.fields[f] || {};
        let v = st.value;
        if (Object.prototype.hasOwnProperty.call(df, f)) {
          const raw = df[f];
          const blank = raw === null || raw === undefined || (typeof raw === 'string' && raw.trim() === '');
          v = fieldKind(f) === 'tick' ? raw === true : (blank ? (st.unit_value ?? null) : String(raw).trim());
        } else if (f === 'flat_day' && st.source == null) {
          // An INHERITED unit Flat tick (ruling A, 2026-11-01) is implied by the Working day, which
          // the server already reads (`!workingDayPicked`) — send only a STORED tick, or a day drafted
          // off Flat previews "Flat day — no overtime" while Save prices its O/T (review 11-01 F1).
          v = null;
        } else if (f === 'unit_wrap' && st.auto && st.unit_value == null) {
          // An auto wrap worked out from THIS day's own call: send it BLANK so the server works it
          // out again from the drafted call (a unit's wrap, typed or auto, is sent as it is).
          v = null;
        }
        req[f === UNIT_FIELD ? 'sub_department_id' : f === 'flat_day' ? 'flat' : f] = f === 'flat_day' ? v === true : v;
      }
      return req;
    });
  const preview = useCalcPreview(previewRequests);

  function getCell(row, date) {
    const day = byDate.get(date);
    const d = draftOf(date);
    if (row.key === 'cost') {
      const priced = submitted ? day.committed : day.cost;
      let text = null;
      if (priced && priced.total != null) text = moneyOrAbsent(priced.total);
      else if (priced && priced.lines) text = 'Rate missing';
      return { text, tone: 'computed', editable: false, title: submitted ? 'The committed (actual) cost of this day' : 'Projected — the server prices the day' };
    }
    if (row.key === 'gag') {
      const drafted = d.gagText !== undefined;
      const v = drafted ? d.gagText : savedJob(day);
      return { text: v || JOB_DEFAULT, tone: v ? 'own' : 'follow', drafted, editable: !readOnly, raw: v, title: v ? (!drafted && day.job_text ? `${v} — a custom job (not a gag)` : v) : 'Pick a gag or type a job (not required)' };
    }
    if (row.key === 'notes') {
      const drafted = d.notes !== undefined;
      const v = drafted ? d.notes : (day.notes || '');
      return { text: v || null, tone: 'own', drafted, editable: !readOnly, raw: v, title: v || 'Notes' };
    }
    const ot = day.overtime || null;
    const livePreview = Object.prototype.hasOwnProperty.call(preview, date);
    if (row.key === 'ot_total') {
      // Owner 2026-10-27: O/T may be MORE or LESS than calculated — the total is what the two rows
      // SAY (overrides included); a difference from the calculation gets ▲ and an explanation.
      const calcT = otTotal(date);
      const cam = effectiveOt(date, 'camera_ot_hours');
      const non = effectiveOt(date, 'non_camera_ot_hours');
      if (cam == null && non == null) return { text: null, tone: 'computed', editable: false, quiet: true };
      const t = Math.round(((cam || 0) + (non || 0)) * 4) / 4;
      const text = t === 0 ? '–' : String(t);
      if (calcT != null && t !== calcT) {
        const diff = Math.round((t - calcT) * 4) / 4;
        return { text: `▲ ${text}`, tone: 'warn', editable: false,
          title: `${Math.abs(diff)}h ${diff > 0 ? 'MORE' : 'LESS'} than calculated.\nCalculated ${calcT}h; entered camera ${cam ?? 0}h + non-camera ${non ?? 0}h = ${t}h.\n↺ on an O/T row puts it back to the calculation.` };
      }
      // quiet: the folded summary already says "O/T n" from the camera/non-camera rows.
      return { text, tone: 'computed', editable: false, quiet: true,
        title: 'The whole day’s overtime. Type Camera O/T and the rest of the calculated total goes to Non-camera (or the other way round); either can then be changed.' };
    }
    if (row.key === 'rest') {
      const r = ot && ot.rest;
      if (!r) return { text: null, tone: 'computed', editable: false, quiet: true };
      return r.short
        // A Flat day shows the short rest but does not pay it (owner 2026-10-28) — say so on the cell.
        ? { text: `▲ ${r.gap_text}${r.unpaid_flat ? ' · not paid' : ''}`, tone: 'warn', editable: false, title: `${r.explain}\n(compensatory rest — check the agreement)` }
        : { text: 'ok', tone: 'computed', editable: false, quiet: true, title: r.explain };
    }
    const f = row.key;
    const kind = fieldKind(f);
    const state = day.fields[f] || { value: null, unit_value: null, source: null, conflicting: false };
    const drafted = !!(d.fields && Object.prototype.hasOwnProperty.call(d.fields, f));
    const draftVal = drafted ? d.fields[f] : undefined;
    if (hasCalc && (ot || livePreview) && OVERRIDE_FIELDS.includes(f)) return overrideCell(row, date, day, state, drafted, draftVal);
    const clearing = drafted && kind !== 'tick' && (draftVal === null || String(draftVal).trim() === '');
    const value = clearing ? state.unit_value : drafted ? draftVal : state.value;
    // Pre-call / Final out / Flat day are judged against `judge_value` (the day's call/wrap when the unit has no own value).
    const unitText = formatFieldValue(f, 'judge_value' in state ? state.judge_value : state.unit_value, unitName);
    const own = !drafted && state.source != null;
    // 🔒 2026-10-25: a buyer's value is READ-ONLY to the crew member (the server refuses it too).
    const locked = mode === 'crew' && !drafted && state.source === 'manager';
    const conflict = !drafted && !!state.conflicting;
    // Server-named (`required_blank`): blank and required on a started/expected working day.
    const required = !conflict && (day.required_blank || []).includes(f) && (!drafted || clearing);
    let title;
    if (conflict) {
      title = isManager
        ? `Their own value — the unit timecard says ${unitText ?? 'nothing'}. Click to choose.`
        : `The unit timecard says ${unitText ?? 'nothing'} — a manager will choose.`;
    } else if (required) title = 'Required';
    else if (clearing) title = 'Will follow the unit timecard once saved';
    else if (own) {
      const whose = state.source === 'manager' ? (isManager ? 'Manager value' : 'Set by a manager') : (isManager ? 'Their own value' : 'Your own value');
      title = unitText != null ? `${whose} · the unit timecard says ${unitText}` : whose;
    } else if (!drafted) title = state.unit_value != null ? 'Follows the unit timecard' : undefined;
    if (locked) {
      const who = state.set_by_name || 'your buyer';
      return {
        text: gridCellText(f, value, unitName), tone: 'locked', locked: true, editable: false,
        raw: kind === 'tick' ? value === true : controlValue(kind, state.value),
        title: `Set by ${who}`, srNote: `set by ${who}, read-only`,
      };
    }
    const cell = {
      text: gridCellText(f, value, unitName),
      tone: clearing ? 'follow' : (drafted || own ? 'own' : 'follow'),
      own,
      conflict,
      drafted,
      // Fill's Clear blanks only what is the person's own or a draft — never an inherited unit value.
      clearable: drafted || own,
      required,
      // Day # is set ONLY on a unit timecard (owner 2026-10-28) — read-only here for everyone.
      editable: !readOnly && f !== 'day_number',
      // 1A: an EDITED Pre-call / Final out commits even when it equals what the cell shows (the
      // unit's); an untouched open-and-leave still commits nothing (the grid's unchanged-drop).
      ...(TYPED_AS_STORED_FIELDS.includes(f) && !drafted && state.source == null ? { commitWhenEdited: true } : {}),
      raw: kind === 'tick' ? value === true : controlValue(kind, drafted ? draftVal : state.value),
      title,
      srNote: conflict ? `the unit timecard says ${unitText ?? 'nothing'}` : required ? 'required' : (own ? (title || '') : undefined),
    };
    // 🔻 The Unit cell's "● on set" badge is DELETED (owner 2026-10-31: "remove the small 'On set'
    // notification next to units"). Camera O/T still follows the shooting unit; only the badge went.
    // THE AUTO WRAP (owner 2026-10-28): no typed wrap → the SERVER's call + standard + lunch, muted
    // with an "auto" word and its sum on hover. A wrap worked out from this day's OWN call follows a
    // drafted call through the preview (manager) or says it is worked out on Save (crew: no preview).
    if (f === 'unit_wrap' && !drafted && state.source == null) {
      const p = livePreview && preview[date] ? preview[date].auto_wrap : null;
      if (state.auto || p) {
        const personLevel = state.unit_value == null;
        let text = value;
        let title = state.auto_explain;
        if (personLevel && livePreview) { text = p ? p.wrap : null; title = p ? p.explain : 'No wrap to work out yet — add the call and working day'; }
        else if (personLevel && !hasCalc && AUTO_WRAP_INPUTS.some((x) => d.fields && Object.prototype.hasOwnProperty.call(d.fields, x))) {
          text = null; title = 'The wrap is worked out again when you save';
        }
        Object.assign(cell, {
          // `fillSkip` (ruling 2A): Fill never types over an auto wrap — the day keeps following its call.
          text, tone: 'computed', badge: 'auto', inherited: true, clearable: false, fillSkip: true, raw: text ?? '',
          title, srNote: 'worked out automatically',
        });
      }
    }
    // Pre-call / Final out: blank = the unit timecard's (a plain follow), else the call / wrap — shown
    // muted (never copied by Fill).
    if ((f === 'in_time' || f === 'out_time') && cell.text == null && !drafted) {
      const fallback = day.fields[f === 'in_time' ? 'unit_call' : 'unit_wrap'];
      if (fallback && fallback.value) {
        cell.text = fallback.value;
        cell.tone = 'follow';
        cell.inherited = true;
        cell.title = `Blank — uses the ${f === 'in_time' ? 'call' : 'wrap'} (${fallback.value}${fallback.auto ? ', worked out automatically' : ''})`;
      }
    }
    if ((f === 'in_time' || f === 'out_time') && ot && ot.flat) {
      cell.tone = 'computed';
      cell.title = 'Flat day — Pre-call / Final out do not count';
    }
    return cell;
  }

  /** A CALCULATED figure (grey italic, the server's sum on hover) or its OVERRIDE (blue, ↺). */
  function overrideCell(row, date, day, state, drafted, draftVal) {
    const f = row.key;
    const kind = fieldKind(f);
    const live = Object.prototype.hasOwnProperty.call(preview, date);
    const p = live && preview[date] ? preview[date][f] : null;
    const saved = (day.overtime || {})[f] || {};
    const fig = live ? { ...saved, calculated: p ? p.calculated : null, explain: p ? p.explain : null } : saved;
    const pending = drafted ? draftVal : state.value;
    const isOverride = pending !== null && pending !== undefined && String(pending).trim() !== '';
    // A calculated zero reads "–" (the mock) and is quiet in the folded summary.
    const calcText = fig.calculated == null ? null
      : kind === 'tick' ? (fig.calculated ? '✓ auto' : '–')
        : (Number(fig.calculated) === 0 ? '–' : String(fig.calculated));
    const conflict = !drafted && !!state.conflicting;
    if (isOverride) {
      const tickNo = kind === 'tick' && pending !== true;
      const calcWords = kind === 'tick' ? (fig.calculated ? 'a penalty' : 'no penalty') : `${fig.calculated ?? 'nothing'}`;
      return {
        text: kind === 'tick' ? (tickNo ? 'No' : '✓') : String(pending),
        tone: 'override', drafted, conflict, quiet: tickNo,
        editable: !readOnly,
        raw: kind === 'tick' ? pending === true : String(pending),
        title: `${state.source === 'self' ? 'Their own value' : 'Override'} — calculated ${calcWords}.${fig.explain ? `\n${fig.explain}` : ''}`,
        onReset: readOnly || conflict ? undefined : () => commitWithRest(row, date, null), // ↺ resets a split PAIR
        srNote: 'override',
      };
    }
    return {
      text: calcText, tone: 'computed', drafted, conflict, quiet: calcText === '–',
      editable: !readOnly && (day.worked || live),
      raw: kind === 'tick' ? !!fig.calculated : (fig.calculated == null ? '' : String(fig.calculated)),
      title: fig.explain ? `${fig.explain}\nClick to override` : (day.worked ? undefined : 'Not a worked day'),
      srNote: drafted ? 'back to the calculation once saved' : 'calculated',
    };
  }

  function onCommit(row, date, raw) {
    const day = byDate.get(date);
    const d = draftOf(date);
    if (row.key === 'gag') {
      const server = savedJob(day);
      const next = { ...d };
      if (raw === server) delete next.gagText; else next.gagText = raw;
      setDraft(date, next);
      return;
    }
    if (row.key === 'notes') {
      const next = { ...d };
      if (raw === (day.notes || '')) delete next.notes; else next.notes = raw;
      setDraft(date, next);
      return;
    }
    const f = row.key;
    const kind = fieldKind(f);
    const state = day.fields[f] || { value: null };
    const fields = { ...(d.fields || {}) };
    if (hasCalc && OVERRIDE_FIELDS.includes(f)) {
      // An override: a value = override it, null/blank = back to the calculation.
      const stored = state.value;
      const storedSet = stored !== null && stored !== undefined;
      const next = raw === null || (kind !== 'tick' && String(raw).trim() === '') ? null : raw;
      const same = next === null ? !storedSet : storedSet && String(next) === String(stored);
      if (same) delete fields[f]; else fields[f] = next;
      setDraft(date, { ...d, fields });
      return;
    }
    // 1A: a FOLLOWING Pre-call / Final out holds nothing of its own — its shown value is the unit's,
    // so a typed equal value is a real (stored) change, never "back to what the server holds".
    const followsOnly = TYPED_AS_STORED_FIELDS.includes(f) && state.source == null;
    const serverRaw = followsOnly ? '' : kind === 'tick' ? state.value === true : controlValue(kind, state.value);
    if (raw === serverRaw) delete fields[f];
    else fields[f] = kind === 'select' ? raw : raw;
    setDraft(date, { ...d, fields });
  }

  const renderConflict = isManager && onResolve ? ({ row, date, close, edit }) => {
    const st = byDate.get(date).fields[row.key];
    const pick = async (choice) => {
      setChooserBusy(true);
      setChooserError(null);
      try {
        const r = await onResolve([{ lineage_id: person.lineage_id, work_date: date, field: row.key }], choice);
        // "Keep editing" on the reason prompt (RC-3): nothing settled, the chooser stays open.
        if (r && r.cancelled) return;
        if (r && r.ok) {
          const skipped = (r.body && r.body.skipped) || [];
          const real = skipped.find((s) => s.reason !== 'not_conflicting');
          if (real) setChooserError(skipReasonText(real.reason)); else close();
        } else setChooserError((r && r.body && r.body.error) || 'Could not save that choice.');
      } catch (e) {
        setChooserError('Could not reach the server. Try again.');
      } finally {
        setChooserBusy(false);
      }
    };
    return (
      <ConflictChooser
        personName={person.name}
        theirText={formatFieldValue(row.key, st.custom_value ?? st.value, unitName)}
        unitText={formatFieldValue(row.key, 'judge_value' in st ? st.judge_value : st.unit_value, unitName)}
        onPick={pick}
        onEdit={readOnly ? null : edit}
        busy={chooserBusy}
        error={chooserError}
        testId={`${idPrefix}-chooser-${row.key}-${date}`}
      />
    );
  } : undefined;

  // A REST day (owner 2026-10-26: "a rest day should clear the times"): picking Rest
  // clears the person's own times, and the unit's times stop showing on that day.
  const dayTypeOf = (date) => {
    const d = draftOf(date);
    if (d.fields && Object.prototype.hasOwnProperty.call(d.fields, 'day_type')) return d.fields.day_type;
    const st = byDate.get(date).fields.day_type;
    return st ? st.value : null;
  };
  /** The day's unit now: a draft, else the stored / unit value. */
  const unitIdOf = (date) => {
    const d = draftOf(date);
    if (d.fields && Object.prototype.hasOwnProperty.call(d.fields, UNIT_FIELD)) return d.fields[UNIT_FIELD];
    const st = byDate.get(date).fields[UNIT_FIELD];
    return st ? st.value : null;
  };
  const workingDayOf = (date) => {
    const d = draftOf(date);
    if (d.fields && Object.prototype.hasOwnProperty.call(d.fields, 'working_day')) return d.fields.working_day;
    const f = byDate.get(date).fields;
    if (f.flat_day && f.flat_day.value === true) return 'flat'; // the old tick still reads as Flat
    return f.working_day ? f.working_day.value : null;
  };
  function cellWithRest(row, date) {
    const cell = getCell(row, date);
    // FLAT (owner 2026-10-27): the times and O/T rows are not needed — greyed, not editable.
    if (FLAT_GREYED_FIELDS.includes(row.key) && workingDayOf(date) === 'flat') return { ...cell, text: OVERRIDE_FIELDS.includes(row.key) ? '–' : null, tone: 'computed', editable: false, drafted: false, conflict: false, quiet: true, onReset: undefined, title: 'Flat day — not needed (a normal day’s pay, no hours measured)' };
    if (row.key === 'day_type' && cell.text == null && !cell.drafted && byDate.get(date).default_rest) return { ...cell, text: 'Rest', tone: 'computed', title: 'Weekend — Rest by default' };
    if (row.key === 'working_day' && !cell.drafted && (dayTypeOf(date) === 'rest' || (dayTypeOf(date) == null && byDate.get(date).default_rest))) return { ...cell, text: null, tone: 'follow', title: 'Rest day — no SWD/SCWD/CWD' };
    if (row.key === 'working_day' && workshopOnShootingUnit(workingDayOf(date), units.find((u) => Number(u.id) === Number(unitIdOf(date))))) {
      return { ...withStandard(cell, date), text: `▲ ${cell.text ?? 'Workshop'}`, tone: 'warn', title: WORKSHOP_ON_SET_WARNING, srNote: WORKSHOP_ON_SET_WARNING };
    }
    if (row.key === 'working_day') {
      // Blank = FLAT (owner 2026-10-27: "Default should be Flat") — no O/T until SWD/SCWD/CWD is picked.
      return withStandard(cell.text == null && !cell.drafted ? { ...cell, text: 'Flat', tone: 'computed', title: BLANK_FLAT_TITLE } : cell, date);
    }
    if (!REST_CLEARS_FIELDS.includes(row.key) || dayTypeOf(date) !== 'rest' || cell.drafted || cell.own) return cell;
    return { ...cell, text: null, inherited: false, tone: 'follow', title: 'Rest day — no times' };
  }
  /** "SWD" → "SWD 11+1": the day's standard hours (the server's label, preview first) after the type. */
  function withStandard(cell, date) {
    if (!hasCalc || cell.text == null || cell.text === 'Flat') return cell;
    const day = byDate.get(date);
    const live = Object.prototype.hasOwnProperty.call(preview, date);
    const label = live ? (preview[date] ? preview[date].standard_label : null) : (day.worked && day.overtime ? day.overtime.standard_label : null);
    if (!label) return cell;
    return { ...cell, text: `${cell.text} ${label}`, title: `${cell.title ? `${cell.title}\n` : ''}Standard day ${label} hours (paid + lunch) — O/T counts after it` };
  }

  /** The day's CALCULATED O/T hours (camera + non-camera, preview first), or null when there is none. */
  function otTotal(date) {
    const day = byDate.get(date);
    const live = Object.prototype.hasOwnProperty.call(preview, date);
    const src = live ? preview[date] : (day.worked ? day.overtime : null);
    if (!src) return null;
    const a = src.camera_ot_hours && src.camera_ot_hours.calculated;
    const b = src.non_camera_ot_hours && src.non_camera_ot_hours.calculated;
    if (a == null && b == null) return null;
    return Math.round((Number(a || 0) + Number(b || 0)) * 4) / 4;
  }
  /** An O/T row's figure as it stands: the override (draft, else stored) if any, else the calculation. */
  function effectiveOt(date, f) {
    const day = byDate.get(date);
    const d = draftOf(date);
    const drafted = !!(d.fields && Object.prototype.hasOwnProperty.call(d.fields, f));
    const pending = drafted ? d.fields[f] : (day.fields[f] || {}).value;
    if (pending !== null && pending !== undefined && String(pending).trim() !== '' && Number.isFinite(Number(pending))) return Number(pending);
    const live = Object.prototype.hasOwnProperty.call(preview, date);
    const src = live ? preview[date] : (day.worked ? day.overtime : null);
    const v = src && src[f] ? src[f].calculated : null;
    return v == null ? null : Number(v);
  }
  const SPLIT_PAIR =Object.freeze({ camera_ot_hours: 'non_camera_ot_hours', non_camera_ot_hours: 'camera_ot_hours' });
  function commitWithRest(row, date, raw) {
    // SPLIT THE HOURS (owner 2026-10-27): typing one O/T row puts the rest of the day's total on
    // the other; ↺ on either puts both back to the calculation.
    if (hasCalc && SPLIT_PAIR[row.key]) {
      const other = SPLIT_PAIR[row.key];
      const blank = raw === null || String(raw).trim() === '';
      const total = otTotal(date);
      onCommit(row, date, raw);
      if (blank) onCommit({ key: other }, date, null);
      else if (total != null && Number.isFinite(Number(raw))) onCommit({ key: other }, date, String(Math.max(0, Math.round((total - Number(raw)) * 4) / 4)));
      return;
    }
    // SWD/SCWD/CWD on a Rest day sets the Day type back to Not set (owner 2026-10-27).
    if (row.key === 'working_day' && raw != null && String(raw).trim() !== '' && dayTypeOf(date) === 'rest') onCommit({ key: 'day_type' }, date, '');
    onCommit(row, date, raw);
    if (row.key !== 'day_type' || raw !== 'rest') return;
    const day = byDate.get(date);
    for (const f of REST_CLEARS_FIELDS) if (day.fields[f] && day.fields[f].source != null) onCommit({ key: f }, date, '');
  }

  /**
   * EDITED SINCE SUBMITTED (owner-approved mock 2026-10-29): on the MANAGER's view of a submitted
   * week, a cell the server names in `day.edited_since_submit` (its committed value differs from
   * what was first submitted) is tinted — and so are the day's O/T total and Day cost when anything
   * on that day moved. A draft over it shows as a draft instead (TimecardCell).
   */
  function cellWithEdit(row, date) {
    const cell = cellWithRest(row, date);
    if (!isManager || !submitted) return cell;
    const edited = byDate.get(date).edited_since_submit || [];
    if (edited.length === 0) return cell;
    const hit = row.key === 'cost' ? true
      : row.key === 'ot_total' ? edited.some((f) => f === 'camera_ot_hours' || f === 'non_camera_ot_hours')
        : row.key === 'gag' ? false : edited.includes(row.key);
    return hit ? { ...cell, edited: true } : cell;
  }

  return (
    <WeekTimecardGrid
      dates={dates}
      today={today}
      groups={withRequiredStars(groups)}
      getCell={cellWithEdit}
      onCommit={commitWithRest}
      fill={!readOnly}
      onConflict={renderConflict ? () => true : undefined}
      renderConflict={renderConflict}
      dayNote={(date) => { const a = byDate.get(date).away; return a ? `Away · ${awayReasonLabel(a.reason)}` : null; }}
      dayComplete={(date) => softDayComplete(byDate.get(date), (drafts || {})[date])}
      idPrefix={idPrefix}
      onStepWeek={onStepWeek}
      nextWeekOpen={nextWeekOpen}
      requestedWeek={requestedWeek}
      jumpTo={jumpTo}
      ariaLabel={`${person.name} — week`}
      timesFold={{ auto: timesNeeded(), label: 'Times' }}
    />
  );

  /**
   * FLAT WEEK (owner 2026-10-27: "if flat for the week should we collapse … and only show this info
   * if they choose something other than flat"): the times start OPEN when any day picks SWD/SCWD/CWD
   * or any time has a value, a draft, a conflict or is required — so nothing typed is ever hidden.
   */
  /** THE * (owner 2026-10-30) — the rule is `timesheetDisplay.js#markRequiredRows`; a person needs a Department too. */
  function withRequiredStars(gs) {
    return markRequiredRows(gs, dates.some((d) => PICKED_WORKING_DAYS.includes(workingDayOf(d))), [UNIT_FIELD]);
  }

  function timesNeeded() {
    if (dates.some((d) => ['swd', 'scwd', 'cwd', 'workshop'].includes(workingDayOf(d)))) return true;
    return groups[1].some((r) => dates.some((d) => {
      const c = cellWithRest(r, d);
      return c.drafted || c.conflict || c.required || (c.text != null && !c.inherited);
    }));
  }
}
